import * as GC from '@grapecity/spread-sheets';
import { IAcquisition } from '@models/acquisition';
import { IStableBalanceCalibration } from '@models/stable-balance/stable-balance-calibration';
import { IAcquisitionCoefficientRecord } from '@models/stable-balance/unadjusted-balance';
import { setBlankCellValue } from '@shared/spread/sheet-functions';
import { borders, colours, CURRENCY_FORMATTER, FONT_NAME, FONT_SIZE, PERCENTAGE_FORMATTER_ROUNDED } from '@shared/spread/spread-settings';
import { getDateForDisplay } from '../helpers';

const POLYNOMIAL_HEADERS = ['Poly-1st', 'Poly-2nd', 'Poly-3rd', 'Poly-4th', 'Poly-5th', 'Poly-6th'];

export const addStableBalanceModelFitToSheet = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  calibration: IStableBalanceCalibration,
  allAcquisitions: IAcquisition[],
  acquisitionCoefficients: IAcquisitionCoefficientRecord[]
): number => {
  try {
    rowIndex = createHeaders(sheet, rowIndex, startCol + 1, calibration, allAcquisitions);
    rowIndex = populateCoefficients(sheet, rowIndex, startCol, calibration, allAcquisitions, acquisitionCoefficients);
  } catch (err) {
    console.error('Error building Stable Balance Model Fit', err);
  }

  return rowIndex;
};

function createHeaders(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  calibration: IStableBalanceCalibration,
  allAcquisitions: IAcquisition[]
): number {
  const rowSpan = 3;
  let colSpan;
  const takenRows = rowSpan;
  let takenCols = 0;

  // β0 column header
  {
    colSpan = 1;
    sheet.getCell(rowIndex, startCol + takenCols).text('β0');
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    takenCols += colSpan;
  }

  // Acquisition Names column headers
  {
    colSpan = 1;
    allAcquisitions.forEach((acq, index) => {
      const col = startCol + takenCols;
      const text = `${acq.AcquisitionName}\n${getDateForDisplay(new Date(acq.AcquisitionDate))}`;
      sheet.getCell(rowIndex, col).text(text);
      sheet.addSpan(rowIndex, col, rowSpan, colSpan);
      takenCols += colSpan;
    });
  }

  // βi && R^2 column headers
  // rowIndex += 2;
  {
    colSpan = 1;
    for (let i = 0; i < 7; i++) {
      const col = startCol + takenCols;

      let text;
      if (i === 6) {
        text = `R^2`;
      } else {
        text = `β${i + 1}`;
      }

      sheet.getCell(rowIndex, col).text(text);
      sheet.addSpan(rowIndex, col, rowSpan, colSpan);
      takenCols += colSpan;
    }
  }

  // Styling
  {
    const topRowArea = sheet.getRange(rowIndex, startCol, takenRows, takenCols);

    topRowArea
      .wordWrap(true)
      .font(`bold ${FONT_SIZE}pt ${FONT_NAME}`)
      .backColor(colours.LIGHT_GREY)
      .vAlign(GC.Spread.Sheets.VerticalAlign.center)
      .hAlign(GC.Spread.Sheets.HorizontalAlign.center);
    topRowArea.setBorder(borders.BLACK_MEDIUM, { outline: true });
  }

  rowIndex += takenRows;
  return rowIndex;
}

function populateCoefficients(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  calibration: IStableBalanceCalibration,
  allAcquisitions: IAcquisition[],
  acquisitionCoefficients: IAcquisitionCoefficientRecord[]
): number {
  let takenRows = 0;

  const betaCoeffCount = calibration.Coefficients[calibration.Coefficients.length - 1].length;

  for (let polyIndex = 0; polyIndex < POLYNOMIAL_HEADERS.length; polyIndex++) {
    const row = rowIndex + takenRows;

    // Row Header (Polynomial Name)
    sheet.getCell(row, startCol).text(`${POLYNOMIAL_HEADERS[polyIndex]}`);

    // Acquisition Coefficients
    allAcquisitions.forEach((acq, acqInd) => {
      const _col = startCol + 2 + acqInd;
      const cell = sheet.getCell(row, _col);
      const acqCoeff = acquisitionCoefficients.find(
        coeff =>
          coeff.AcquisitionId === acq.AcquisitionId &&
          coeff.PolynomialOrder === polyIndex + 1 &&
          // Fix 8245
          calibration.SelectedAcquisitionIds.includes(coeff.AcquisitionId)
      );
      const cellValue = acqCoeff ? acqCoeff.Coefficient : null;

      if (cellValue) {
        cell.value(cellValue);
      } else {
        setBlankCellValue(cell);
      }
    });

    // B0, B1-B6, R2 (RSquared)

    const polyCoeff = calibration.Coefficients[polyIndex] || [];

    for (let coeffIndex = 0; coeffIndex < betaCoeffCount; coeffIndex++) {
      const _row = rowIndex + polyIndex;
      let _col = startCol + 1; // B0 Column
      let cellValue;
      if (polyCoeff) {
        cellValue = polyCoeff[coeffIndex];
      }

      // RSquared
      if (coeffIndex === polyCoeff.length - 1) {
        _col += allAcquisitions.length + betaCoeffCount - 1;
      } else if (coeffIndex > 0) {
        // Beta Coefficients B1-B6
        _col += allAcquisitions.length + coeffIndex;
        // Moving the RSquared makes this a bit tricky
        if (coeffIndex > polyCoeff.length - 1) {
          _col -= 1;
        }
      }
      // Else: B0
      const cell = sheet.getCell(_row, _col);

      if (cellValue) {
        cell.value(cellValue);
      } else {
        setBlankCellValue(cell);
      }
    }

    takenRows++;
  }

  const takenCols = betaCoeffCount + allAcquisitions.length;

  // Styling

  // -- Table Content
  const contentRange = sheet.getRange(rowIndex, startCol + 1, takenRows, takenCols);
  contentRange.setBorder(borders.GREY_NORMAl, { inside: true });
  contentRange.setBorder(borders.BLACK_MEDIUM, { outline: true });
  contentRange.formatter(CURRENCY_FORMATTER);
  contentRange.vAlign(GC.Spread.Sheets.VerticalAlign.center);
  contentRange.hAlign(GC.Spread.Sheets.HorizontalAlign.center);

  // -- Row Headers
  const rowHeadersRange = sheet.getRange(rowIndex, startCol, takenRows, 1);
  rowHeadersRange
    .wordWrap(true)
    .font(`bold ${FONT_SIZE}pt ${FONT_NAME}`)
    .backColor(colours.LIGHT_GREY)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center);
  rowHeadersRange.setBorder(borders.BLACK_MEDIUM, { outline: true });

  // Adding Highlighting for estimate row
  const highlightColour = colours.LIGHT_GREEN;
  const selectedPolyRange = sheet.getRange(rowIndex + calibration.SelectedPolynomialForm, startCol, 1, takenCols + 1);
  selectedPolyRange.backColor(highlightColour);

  // Acquisition highlighting
  allAcquisitions.forEach((acq, index) => {
    // For some reason the array of id's is a string needs to be converted
    // @ts-ignore
    const selectedAcquisitionIdsArr = calibration.SelectedAcquisitionIds.split(',').map(Number);
    if (selectedAcquisitionIdsArr.findIndex(acqId => acqId === acq.AcquisitionId) >= 0) {
      const acquisitionRange = sheet.getRange(rowIndex - 1, startCol + 2 + index, takenRows + 1, 1);
      acquisitionRange.backColor(highlightColour);
    }
  });

  // RSquared (R2) Formatting overwrite
  const rSquaredRange = sheet.getRange(rowIndex, startCol + takenCols, takenRows, 1);
  rSquaredRange.formatter(PERCENTAGE_FORMATTER_ROUNDED);

  rowIndex += takenRows;

  return rowIndex;
}
